<template>
  <div>
    <header class="page-header">
      <div class="page-header__logo">
        <div class="page-header-logo__text"><b>iBot</b> Панель управления</div>
      </div>

      <section class="page-header__content">
        <div class="page-header__left page-header__container">
          <hamburger
            id="hamburger-container"
            :is-active="!sidebar.opened"
            class="hamburger-container"
            @toggleClick="toggleSideBar"
          />

          <breadcrumb id="breadcrumb-container" class="breadcrumb-container">
            <template #project-switcher>
              <el-breadcrumb-item v-if="$route.params.project_id" class="project-switcher-item ms-2">
                <el-cascader
                  v-model="selectedProject"
                  :options="projectOptions"
                  placeholder="Переключить проект"
                  @change="onProjectChange"
                  style="min-width:200px"
                />
              </el-breadcrumb-item>
            </template>
          </breadcrumb>
        </div>

        <div class="page-header__right page-header__container">
          <!-- БЛОК КЛИКОВ -->
          <div class="header-balance">
            <div class="header-balance__transactions" @click="goToClickTransactions">
              <span class="material-symbols-rounded cursor-pointer">touch_app</span>
              <span class="header-balance__sum">{{ clicks }}</span>
            </div>
            <div class="header-balance__recharge" @click="goToClickPayment">
              <i class="el-icon-circle-plus-outline"></i>
            </div>
          </div>

          <!-- БЛОК БАЛАНСА -->
          <div class="header-balance">
            <div class="header-balance__transactions" @click="goToTransactions">
              <i class="header-balance__icon el-icon-coin"></i>
              <span class="header-balance__sum">{{ balance }} &#8381;</span>
            </div>
            <div class="header-balance__recharge" @click="goToPayment">
              <i class="el-icon-circle-plus-outline"></i>
            </div>
          </div>

          <el-badge :value="processes.length" class="item" type="primary">
            <el-button size="small" @click="openProcessBar">
              <i class="el-icon-data-analysis"></i>
              Процессы
            </el-button>
          </el-badge>

          <el-dropdown class="avatar-container right-menu-item hover-effect" trigger="click">
            <div class="avatar-wrapper">
              <img src="/assets/admin/images/no-avatar-user.png" class="user-avatar" />
              <span class="avatar-name">{{ name }}</span>
              <i class="el-icon-caret-bottom" />
            </div>
            <el-dropdown-menu slot="dropdown">
              <router-link v-show="userId !== null" to="/profile">
                <el-dropdown-item>{{ $t('navbar.profile') }}</el-dropdown-item>
              </router-link>
              <el-dropdown-item divided>
                <span @click="logout">{{ $t('navbar.logOut') }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </section>
    </header>

    <click-purchase-modal ref="clickModal" />

    <ProcessBar
      v-if="dialogVisible"
      :processes="processes"
      :queue-overview="queueOverview"
      class="header-processes-dropdown"
      @processCompletedAll="dialogVisible = false"
      @open-state-change="onPBOpenStateChange"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Hamburger from '@/admin/js/components/Hamburger';
import Breadcrumb from '@/admin/js/components/Breadcrumb';
import ClickPurchaseModal from '@/admin/js/views/click-transactions/components/ClickPurchaseModal.vue';
import ProcessBar from '@/admin/js/components/ProcessBar/index.vue';
import useProcesses from '@/admin/js/services/useProcesses.js';
import { loadSettings, saveSettings } from '@/admin/js/services/tableSettings';

export default {
  name: 'Header',
  components: { Hamburger, Breadcrumb, ClickPurchaseModal, ProcessBar },

  data() {
    return {
      selectedProject: [],
      dialogVisible: false,
      processes: [],
      _stopProcWatch: null,
      _echoUserCh: null,
      queueOverview: null,
      _queueTimer: null,
    };
  },

  computed: {
    ...mapGetters(['sidebar', 'userId', 'name', 'balance', 'clicks']),
    ...mapGetters('projectsTree', ['options']),
    projectOptions() { return this.options || []; },
  },

  watch: {
    '$route.params.project_id': {
      handler() { this.setSelectedProject(); },
      immediate: true
    },
    projectOptions() {
      this.$nextTick(() => this.setSelectedProject());
    }
  },

  methods: {
    async fetchQueueOverview() {
      try {
        const { data } = await axios.get('/api/admin/queues/overview');
        this.queueOverview = data;
      } catch (e) {}
    },
    toggleSideBar() { this.$store.dispatch('app/toggleSideBar'); },

    async logout() {
      await this.$store.dispatch('user/logout');
      this.$router.push(`/login?redirect=${encodeURIComponent(this.$route.fullPath)}`);
    },

    goToTransactions() { this.$router.push('/profile/transactions'); },
    goToPayment() { this.$router.push('/profile/payment'); },
    goToClickTransactions() { this.$router.push('/profile/click-transactions'); },
    goToClickPayment() { this.$refs.clickModal.open(); },

    onProjectChange(path) {
      const newId = Array.isArray(path) ? Number(path[path.length - 1]) : Number(path);
      const tab = this.$route.query.tab || 'keys';
      window.location.href = `/admin/projects/${newId}?tab=${tab}`;
    },

    async refreshProjectsTree(force = false) {
      await this.$store.dispatch('projectsTree/fetch', { force });
    },

    setSelectedProject() {
      const pid = Number(this.$route.params.project_id);
      if (!pid || !this.projectOptions.length) {
        this.selectedProject = [];
        return;
      }
      for (const grp of this.projectOptions) {
        if (grp.children && grp.children.some(p => p.value === pid)) {
          this.selectedProject = [grp.value, pid];
          return;
        }
      }
      this.selectedProject = [];
    },

    async openProcessBar() {
      const ui = await loadSettings('processbar.ui', {});
      const next = { ...ui, openState: 'window', minimized: false };
      await saveSettings('processbar.ui', next).catch(() => { });

      this.dialogVisible = true;
    },

    onPBOpenStateChange(state) {
      if (state === 'closed') this.dialogVisible = false;
    },
  },

  async mounted() {
    const { userId } = this;

    this._stopProcWatch = useProcesses({
      userId,
      onChange: list => { this.processes = list; },
      onStart: p => this.$notify({ title: 'Запущен процесс', message: p.name, type: 'info', duration: 7000 }),
      onCompleted: p => {
        this.$notify({ title: 'Процесс завершён', message: p.name, type: 'success', duration: 5000 });
        if (this.processes.length === 0) this.dialogVisible = false;
      },
      onCancelled: p => this.$notify({ title: 'Процесс отменён', message: p.name, type: 'warning', duration: 5000 }),
    });

    if (userId && window.Echo?.private) {
      this._echoUserCh = window.Echo.private(`user.${userId}`)
        .listen('.user.wallet.updated', () => {
          this.$store.dispatch('user/getInfo').catch(() => { });
        });
    }

    await this.refreshProjectsTree(false);
    this.setSelectedProject();
    this.fetchQueueOverview();
    this._queueTimer = setInterval(this.fetchQueueOverview, 30000);

    try {
      const ui = await loadSettings('processbar.ui', {});
      this.dialogVisible = ui && (ui.openState === 'window' || ui.openState === 'tray');
    } catch {}
  },

  beforeDestroy() {
    this._stopProcWatch && this._stopProcWatch();
    if (this._echoUserCh) {
      this._echoUserCh.stopListening('.user.wallet.updated');
      this._echoUserCh = null;
    }
    if (this._queueTimer) clearInterval(this._queueTimer);
  }
};
</script>

<style lang="scss" scoped>
.hamburger-container {
  position: relative;
  cursor: pointer;
  width: 37px;
  height: 18px;
  overflow: hidden;
}

.avatar-container {
  margin-right: 30px;
  .avatar-wrapper {
    position: relative;
    .avatar-name { margin-left: 4px; }
    .user-avatar {
      cursor: pointer;
      width: 30px;
      height: 30px;
      border-radius: 50%;
    }
    .el-icon-caret-bottom {
      cursor: pointer;
      position: absolute;
      right: -20px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 12px;
    }
  }
}

.header-balance {
  display: inline-flex;
  align-items: center;
  background-color: #f2f2f2;
  border-radius: 8px;

  &__transactions {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    padding: 6px 12px;
    border-right: 1px solid #ccc;
    &:hover .header-balance__sum { text-decoration: underline; }
    .header-balance__icon { margin-right: 6px; font-size: 16px; }
    .header-balance__sum { font-weight: 500; }
  }

  &__recharge {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    padding: 8px 12px;
    i {
      border-radius: 50%;
      color: #20b805;
      font-size: 25px;
    }
    &:hover i { background-color: rgba(32, 184, 5, 0.1); }
  }
}

.project-switcher .el-cascader { min-width: 200px; }
</style>
